import { useCallback, useMemo, useState, useContext } from 'react';
import { css } from '@emotion/react';
import { VoicemailGreetingType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { VoicemailBox } from '@weave/schema-gen-ts/dist/schemas/phone/voicemail-config/v2/voicemail_config.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  Button,
  DropdownField,
  SkeletonLoaders,
  useAlert,
  useFormField,
  Text,
  useModalControl,
  useMultiStepModalControl,
  useTooltip,
} from '@frontend/design-system';
import { queryKeys } from '../../../../query-keys';
import {
  AudioPicker,
  AudioPickerProps,
  DEFAULT_GREETING_ID,
  DEFAULT_GREETING_PATH,
} from '../../../audio-picker/audio-picker';
import { AudioItem } from '../../../audio-picker/types';
import { CreateModal } from '../../../voicemail-box/new/create-button';
import { VoicemailBoxCreateResponse } from '../../../voicemail-box/new/types';
import { VoicemailSettingTray } from '../../voicemail-setting-tray';
import { hasVoicemailExpansion } from '../call-route-flow/data';
import { Step } from './atoms/step';
import { StepContext } from './provider';
import { AddStepPanelProps } from './types';

export const voicemailAllowedOptions: AudioPickerProps['allowedOptions'] = {
  add: true,
  custom: true,
  standard: false,
  mailbox: true,
  none: true,
  default: true,
};

type GroupedVoicemailBoxes = {
  linkedVoicemailBox?: VoicemailBox;
  voicemailBoxes: VoicemailBox[];
};

const EmailTag = ({ email }: { email: string }) => {
  const trimmedEmail = email.trim();
  const shouldShowTooltip = trimmedEmail.length > 50;
  const displayEmail = trimmedEmail.length > 50 ? `${trimmedEmail.slice(0, 50)}...` : trimmedEmail;
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();

  return (
    <div>
      <div
        css={{
          backgroundColor: theme.colors.primary5,
          borderRadius: theme.borderRadius.medium,
          padding: theme.spacing(0.5),
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        {...(shouldShowTooltip ? triggerProps : {})}
      >
        {displayEmail}
      </div>
      <Tooltip {...tooltipProps}>{shouldShowTooltip ? trimmedEmail : null}</Tooltip>
    </div>
  );
};

export const VoicemailStep = ({
  tenantId,
  settingsTenantLocation,
  initialState,
  callObjectsData,
  isDeviceExtensionRoute = false,
  callRoute,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const stepContext = useContext(StepContext);
  const { isSingleTypeScope, getScopeName } = useAppScopeStore();

  const styles = {
    borderedCard: {
      borderRadius: theme.borderRadius.medium,
      padding: theme.spacing(2),
      border: `1px solid ${theme.colors.neutral30}`,
    },
    solidCard: {
      backgroundColor: theme.colors.neutral5,
      borderRadius: theme.borderRadius.medium,
      padding: theme.spacing(2),
    },
    flexRow: {
      display: 'flex',
      gap: theme.spacing(2),
    },
    flexRowWrap: {
      display: 'flex' as const,
      gap: theme.spacing(1),
      flexWrap: 'wrap' as const,
    },
  };

  const { triggerProps, ...createVoicemailModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const voicemailBoxTrayModalControl = useModalControl();

  const handleOpenCreateVoicemailBox = () => {
    createVoicemailModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };

  const [selectedAudioFile, setSelectedAudioFile] = useState<AudioItem | undefined>(undefined);

  const { voicemailBoxes = [], voicemailBoxesIsLoading } = callObjectsData;

  const linkedVoicemailBoxId = callRoute?.deviceExtension?.voicemailBoxId;

  const groupedVoicemailBoxes = useMemo(() => {
    const grouped = voicemailBoxes.reduce<GroupedVoicemailBoxes>(
      (acc, box) => {
        if (isDeviceExtensionRoute && box.mailbox.id === linkedVoicemailBoxId) {
          acc.linkedVoicemailBox = box;
        } else {
          acc.voicemailBoxes.push(box);
        }

        return acc;
      },
      { voicemailBoxes: [] }
    );
    return grouped;
  }, [voicemailBoxes]);

  const getInitialGreetingMediaValue = () => {
    const callObject = initialState?.callObject;

    if (!hasVoicemailExpansion(callObject)) {
      return 'default';
    }

    const greetingId = callObject.voicemailExpansion.greetingMediaItemId;
    const greetingType = callObject.voicemailExpansion.greetingType;

    if (greetingType === VoicemailGreetingType_Enum.NO_GREETING) {
      return 'none';
    }

    if (greetingType === VoicemailGreetingType_Enum.DEFAULT_GREETING) {
      return 'default';
    }

    if (!greetingId) {
      return 'default';
    }

    return greetingId;
  };

  const voicemailBoxProps = useFormField({
    required: true,
    type: 'dropdown',
    value: initialState?.callObject?.primitiveId,
  });

  const greetingMediaProps = useFormField({
    required: true,
    type: 'dropdown',
    value: getInitialGreetingMediaValue(),
  });

  const { data: selectedVoicemailBoxData, isLoading: isSelectedVoicemailBoxLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.readVoicemailBox(voicemailBoxProps.value)],
    queryFn: () => VoicemailConfigAPI.Read({ voicemailBoxId: voicemailBoxProps.value }),
    enabled: !!voicemailBoxProps.value && !!settingsTenantLocation?.phoneTenantId,
    staleTime: 5 * 60 * 1000,
  });

  const selectedVoicemailBox = selectedVoicemailBoxData?.voicemailbox;

  const getShareMessage = (isShared: boolean) => {
    if (isSingleTypeScope) {
      return isShared
        ? t("This Voicemail Box's messages are shared with all users.")
        : t("This Voicemail Box's messages are not shared with all users.");
    }

    // Unify Location scenarios - use voicemail box's locationIds
    const voicemailLocationIds = selectedVoicemailBox?.locationIds || [];
    const totalLocations = settingsTenantLocation?.children.length;
    const selectedCount = voicemailLocationIds.length;

    // When not shared, always show generic message
    if (!isShared) {
      return t("This Voicemail Box's messages are not shared with users in all locations.");
    }

    // When shared with all locations or no specific locations (means all)
    if (selectedCount === 0 || selectedCount === totalLocations) {
      return t("This Voicemail Box's messages are shared with users in all locations.");
    }

    // When shared with specific locations, show details
    const locationNames = voicemailLocationIds.map((id) => getScopeName(id)).filter(Boolean);

    if (selectedCount === 1) {
      return t("This Voicemail Box's messages are shared with users in {{locationName}}.", {
        locationName: locationNames[0],
      });
    }

    if (selectedCount >= 4) {
      const firstThree = locationNames.slice(0, 3);
      const remaining = selectedCount - 3;
      const locationList = firstThree.join(', ');
      return t("This Voicemail Box's messages are shared with users in {{locationList}}, + {{count}} more.", {
        locationList,
        count: remaining,
      });
    }

    // TODO: This manual list formatting won't work correctly for i18n translations
    // since other languages have different ways of listing items (comma placement, conjunctions, etc.)
    const locationList =
      locationNames.length === 2
        ? locationNames.join(' and ')
        : `${locationNames.slice(0, -1).join(', ')}, and ${locationNames[locationNames.length - 1]}`;
    return t("This Voicemail Box's messages are shared with users in {{locationList}}.", { locationList });
  };

  const handleProceedClick = useCallback(() => {
    const voicemailBox = voicemailBoxes?.find((vm) => vm.mailbox.id === voicemailBoxProps.value);

    if (!voicemailBox) {
      alerts.error('Unknown voicemail box data');
      sentry.error({
        error: 'Voicemail box not found in Voicemail step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            voicemailBoxes,
            selectedVoicemailBoxId: voicemailBoxProps.value,
          },
        },
      });
      return;
    }

    let greetingMediaFileName;
    let greetingMediaItemId;
    let downloadUrl;
    let greetingType;
    if (greetingMediaProps.value === 'none') {
      greetingMediaFileName = '';
      greetingMediaItemId = '';
      downloadUrl = '';
      greetingType = VoicemailGreetingType_Enum.NO_GREETING;
    } else if (greetingMediaProps.value === 'default') {
      greetingMediaFileName = 'Default Greeting';
      greetingMediaItemId = DEFAULT_GREETING_ID;
      downloadUrl = DEFAULT_GREETING_PATH;
      greetingType = VoicemailGreetingType_Enum.DEFAULT_GREETING;
    } else if (selectedAudioFile) {
      greetingMediaFileName = selectedAudioFile.name;
      greetingMediaItemId = selectedAudioFile.id;
      downloadUrl = selectedAudioFile.path;
      if (selectedAudioFile.type === 'mailbox') {
        greetingType = VoicemailGreetingType_Enum.NUMBERED_GREETING;
      } else {
        greetingType = VoicemailGreetingType_Enum.AUDIO_LIBRARY_GREETING;
      }
    } else {
      alerts.error('Unknown greeting data');
      return;
    }

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX,
      callObject: {
        primitiveId: voicemailBox.mailbox.id,
        primitiveName: voicemailBox.mailbox.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        voicemailExpansion: {
          greetingMediaFileName,
          greetingMediaItemId,
          downloadUrl,
          greetingType,
          sharedAccess: selectedVoicemailBox?.mailbox.sharedAccess,
          sendNotification: selectedVoicemailBox?.mailbox.sendNotification,
          emailAddresses: selectedVoicemailBox?.mailbox.email
            ? selectedVoicemailBox.mailbox.email
                .split(',')
                .map((email) => email.trim())
                .filter(Boolean)
            : [],
          smsNumbers: selectedVoicemailBox?.mailbox.sms
            ? selectedVoicemailBox.mailbox.sms
                .split(',')
                .map((sms) => sms.trim())
                .filter(Boolean)
            : [],
        },
      },
    });
  }, [
    alerts,
    voicemailBoxes,
    voicemailBoxProps.value,
    greetingMediaProps.value,
    selectedAudioFile,
    selectedVoicemailBox,
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
  ]);

  const handleOnCreateSuccess = (data: VoicemailBoxCreateResponse) => {
    const mailBoxId = data.mailbox.id;

    voicemailBoxProps.onChange({
      // @ts-expect-error This is complaining thinking that value does not exist on the event target,
      // but it does.
      target: { value: mailBoxId },
    });
  };

  return (
    <>
      <Step
        title={t('Voicemail')}
        primaryButtonDisabled={!voicemailBoxProps.value || !greetingMediaProps.value}
        onPrimaryButtonClick={handleProceedClick}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(3)};
          `}
        >
          <SkeletonLoaders.Loader isLoading={voicemailBoxesIsLoading} shape='rectangle' width={320} height={50}>
            <DropdownField
              {...voicemailBoxProps}
              css={css`
                max-width: 300px;
              `}
              name='voicemailBoxId'
              label={t('Select Voicemail Box')}
            >
              <DropdownField.Option
                css={css`
                  padding: 0;
                `}
                key={'create-voicemail-box'}
                value=''
                isSelectable={false}
              >
                <Button
                  iconName='plus'
                  variant='tertiary'
                  onClick={handleOpenCreateVoicemailBox}
                  css={css`
                    width: 100%;
                    justify-content: start;
                  `}
                >
                  {t('Create Voicemail Box')}
                </Button>
              </DropdownField.Option>

              {!!groupedVoicemailBoxes.linkedVoicemailBox && (
                <DropdownField.Option
                  key={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.id}
                  value={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.id}
                  displayValue={groupedVoicemailBoxes.linkedVoicemailBox.mailbox.name}
                >
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: ${theme.spacing(0.5)};
                    `}
                  >
                    <Text as={'span'}>{groupedVoicemailBoxes.linkedVoicemailBox.mailbox.name}</Text>
                    <Text as={'span'} size='small' color='subdued'>
                      {t('Linked Voicemail Box')}
                    </Text>
                  </div>
                </DropdownField.Option>
              )}

              {groupedVoicemailBoxes.voicemailBoxes?.map((option) => (
                <DropdownField.Option key={option.mailbox.id} value={option.mailbox.id}>
                  {option.mailbox.name}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </SkeletonLoaders.Loader>

          {voicemailBoxProps.value && (
            <>
              <AudioPicker
                widths={{ field: '300px', scrubber: '240px' }}
                allowedOptions={voicemailAllowedOptions}
                mailboxId={voicemailBoxProps.value}
                field={greetingMediaProps}
                name='greetingMediaId'
                labels={{
                  field: t('Select Voicemail Greeting'),
                }}
                onSelect={(item) => {
                  // NOTE: this callback function will not be called if the user selects the "No Greeting option". The
                  // value will still be set on the field (greetingMediaProps.value), but the onSelect function will not
                  // be called.
                  setSelectedAudioFile(item);
                }}
                tenantId={tenantId}
              />

              {selectedVoicemailBox && !isSelectedVoicemailBoxLoading ? (
                <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text weight='bold' size='large'>
                    {t('Voicemail Box Preview')}
                  </Text>
                  <Button
                    variant='secondary'
                    onClick={() => voicemailBoxTrayModalControl.openModal()}
                    trackingId='phone::setting::call-route:edit-voicemailbox-btn'
                  >
                    <Icon name='edit-small' />
                    {t('Edit Voicemail Box')}
                  </Button>
                </div>
              ) : (
                voicemailBoxProps.value && (
                  <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text weight='bold' size='large'>
                      {t('Voicemail Box Preview')}
                    </Text>
                    <Button
                      variant='secondary'
                      disabled={true}
                      trackingId='phone::setting::call-route:edit-voicemailbox-btn'
                    >
                      <Icon name='edit-small' />
                      {t('Loading...')}
                    </Button>
                  </div>
                )
              )}
              {selectedVoicemailBox && !isSelectedVoicemailBoxLoading ? (
                <>
                  {selectedVoicemailBox?.mailbox.sharedAccess ? (
                    <div css={styles.borderedCard}>
                      <div css={styles.flexRow}>
                        <Icon name='check-please' />
                        <div>
                          <Text size='large' weight='bold'>
                            {t('Share in App is enabled')}
                          </Text>
                          <Text size='medium'>{getShareMessage(true)}</Text>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div css={styles.solidCard}>
                      <div css={styles.flexRow}>
                        <Icon name='opt-out-alert' color='disabled' />
                        <div>
                          <Text size='large' weight='bold'>
                            {t('Share in App is disabled')}
                          </Text>
                          <Text size='medium'>{getShareMessage(false)}</Text>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedVoicemailBox?.mailbox.sendNotification ? (
                    <div css={styles.borderedCard}>
                      <div css={styles.flexRow}>
                        <Icon name='check-please' />
                        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                          <div>
                            <Text weight='bold'>{t('Email and SMS Notifications are enabled')}</Text>
                            <Text size='medium'>{t('Receive a notification when you have a new voicemail.')}</Text>
                          </div>
                          <Text size='medium' weight='bold'>
                            {t('Notification Destinations:')}
                          </Text>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
                            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                              <Icon name='email' color='subdued' />
                              <Text size='medium' weight='bold'>
                                {t('Email')}
                              </Text>
                            </div>
                            <div css={styles.flexRowWrap}>
                              {selectedVoicemailBox?.mailbox.email?.split(',').map((email, index) => (
                                <EmailTag key={index} email={email} />
                              ))}
                            </div>
                          </div>
                          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
                            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                              <Icon name='message' color='subdued' />
                              <Text size='medium' weight='bold'>
                                {t('SMS Numbers')}
                              </Text>
                            </div>
                            <div css={styles.flexRowWrap}>
                              {selectedVoicemailBox?.mailbox.sms?.split(',').map((sms, index) => {
                                const formattedSms = formatPhoneNumber(sms);

                                return (
                                  <div
                                    key={index}
                                    css={{
                                      backgroundColor: theme.colors.primary5,
                                      borderRadius: theme.borderRadius.medium,
                                      padding: theme.spacing(0.5),
                                      width: 'fit-content',
                                    }}
                                  >
                                    {formattedSms}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div css={styles.solidCard}>
                      <div css={styles.flexRow}>
                        <Icon name='opt-out-alert' color='disabled' />
                        <div>
                          <Text size='large' weight='bold'>
                            {t('Email & SMS Notifications are disabled')}
                          </Text>
                          <Text size='medium'>{t('Notifications are not sent when you receive a new voicemail.')}</Text>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                voicemailBoxProps.value && (
                  <div css={{ padding: theme.spacing(2), textAlign: 'center' }}>
                    <Text>{t('Loading voicemail box details...')}</Text>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </Step>

      {settingsTenantLocation && (
        <CreateModal
          {...createVoicemailModalProps}
          tenantLocation={settingsTenantLocation}
          onSuccess={handleOnCreateSuccess}
        />
      )}

      {/* Voicemail Box Settings Tray */}
      <VoicemailSettingTray
        modalControls={voicemailBoxTrayModalControl}
        mountTarget='#call-route-page-body-container'
        selectedVoicemailBox={selectedVoicemailBox}
        stepContext={stepContext}
      />
    </>
  );
};
